import * as React from 'react';
import { throttle } from 'lodash';
import { useCallback } from 'react';
import colors from '../helpers/colors';
import {
  interruptKernel,
  changeKernel,
  restartKernel,
  ensureKernel,
  // heartbeatKernel,
  runAllCells,
  destroyKernel,
  startKernel,
} from '../resources/api';
import '../css/animations.scss';
import { initializeCodePresence } from '../components/CodePresence';
import Dropdown from '../components/Dropdown';
import { Modal } from '../components/Modal';
import '../css/CodeFieldDropdownWrapper.scss';
import {
  DEFAULT_CODE_KERNEL_CONFIG,
  KERNEL_CONFIG_LIST,
} from '../authoring/components/Block/KernelSettings';

// const KERNEL_KEEPALIVE_HEARTBEAT_INTERVAL_MSEC = 60 * 1000;
const KERNEL_THROTTLE_DELAY_MSEC = 6000;

const styles = {
  container: {
    backgroundColor: colors.gray1,
    width: '100%',
    zIndex: 4, // minimum z-index to cover code
    color: 'var(--black-tint-40)',
  },
  content: {
    textTransform: 'capitalize',
    width: '1200px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  kernelCTA: (disabled) => ({
    color: disabled ? 'var(--black-shade-20)' : 'var(--blue-shade-20)',
    cursor: disabled ? 'inherit' : 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  }),
};

const KernelCTA = ({ kernelState, docId }) => {
  const [kernelControlDisabled, setKernelControlsDisabled] =
    React.useState(false);
  // Currently if the restart command is clicked quickly in succession before the prior restart is processed fully,
  // the worker appears to get in a broken state. While the underlying issue needs to be investigated further to make
  // restarting more robust, having the restart command throttled should prevent the vast majority of issues.
  // There is no actual use case that would require clicking the restart button in quick succession.
  const throttledRestartKernel = useCallback(
    throttle((docId) => restartKernel(docId), KERNEL_THROTTLE_DELAY_MSEC, {
      leading: true,
      trailing: false,
    }),
    [docId, setKernelControlsDisabled]
  );
  // The start command is throttled to prevent the user from starting the kernel multiple times in quick succession, as
  // the start command will interrupt any prior commands that are in the queue.
  const throttledStartKernel = useCallback(
    throttle((docId) => startKernel(docId), KERNEL_THROTTLE_DELAY_MSEC, {
      leading: true,
      trailing: false,
    }),
    [docId, setKernelControlsDisabled]
  );

  if (kernelState === 'unavailable' || kernelState === 'initial') {
    return (
      <button
        type="button"
        style={styles.kernelCTA(kernelControlDisabled)}
        onClick={() => {
          setKernelControlsDisabled(true);
          setTimeout(
            () => setKernelControlsDisabled(false),
            KERNEL_THROTTLE_DELAY_MSEC
          );
          throttledStartKernel(docId);
        }}
        disabled={kernelControlDisabled}
      >
        Start
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        style={styles.kernelCTA(kernelControlDisabled)}
        onClick={() => {
          setKernelControlsDisabled(true);
          setTimeout(
            () => setKernelControlsDisabled(false),
            KERNEL_THROTTLE_DELAY_MSEC
          );
          if (kernelState === 'busy') {
            interruptKernel(docId);
          }
          throttledRestartKernel(docId);
        }}
      >
        Restart
      </button>
      &nbsp;|&nbsp;
      {/* We display the interrupt button even when no code is running, because it resets
      some of the state in WKM which often helps get kernels unstuck */}
      <button
        type="button"
        style={styles.kernelCTA(kernelControlDisabled)}
        onClick={() => interruptKernel(docId)}
        disabled={kernelControlDisabled}
      >
        Interrupt
      </button>
    </>
  );
};

const ConfigDropdown = ({ currentItem, selections, name }) => (
  <Dropdown>
    {({ isActive, toggle }) => (
      <>
        <button
          type="button"
          className="button-secondary mb6 px3"
          onClick={toggle}
          data-cy={`select-${name}`}
          style={{ textAlign: 'left', minWidth: '150px' }}
        >
          <span className="flex justify-between ml2">
            <span>{currentItem}</span>
            <span>▾</span>
          </span>
        </button>
        <div style={{ position: 'absolute', marginTop: '2.25rem' }}>
          <ul style={{ display: isActive ? 'block' : 'none' }}>
            {selections(toggle)}
          </ul>
        </div>
      </>
    )}
  </Dropdown>
);

const KernelConfigChoice = ({
  currentLang,
  currentResourceConfig,
  onSubmit,
  onCancel,
}) => {
  const kernelLangList = ['Python 3', 'R', 'SageMath', 'Julia'];
  const [lang, setLang] = React.useState(currentLang);
  const [resourceConfig, setResourceConfig] = React.useState(
    currentResourceConfig
  );
  return (
    <div>
      <div className="bold mb2 text-black-tint-40">Selected Language</div>
      {/* Mostly the same as authoring/ui/src/components/Block/KernelSettings.js:KernelLang */}
      <ConfigDropdown
        currentItem={lang || kernelLangList[0]}
        name="lang"
        selections={(toggle) => (
          <>
            {kernelLangList.map((lang) => (
              <li
                key={lang}
                onClick={() => {
                  toggle();
                  setLang(lang);
                }}
                data-cy={`select-lang-${lang}`}
              >
                {lang}
              </li>
            ))}
          </>
        )}
      />
      <div className="bold mb2 text-black-tint-40">
        Selected Resource Configuration
      </div>
      <ConfigDropdown
        currentItem={
          KERNEL_CONFIG_LIST[resourceConfig || DEFAULT_CODE_KERNEL_CONFIG]
            .resources
        }
        name="resource"
        selections={(toggle) => (
          <>
            {Object.keys(KERNEL_CONFIG_LIST).map((key) => (
              <li
                key={key}
                onClick={() => {
                  toggle();
                  setResourceConfig(key);
                }}
                data-cy={`select-config-${key}`}
              >
                {KERNEL_CONFIG_LIST[key].resources}
              </li>
            ))}
          </>
        )}
      />
      <div className="flex">
        <button
          type="button"
          className="button-primary mr4"
          onClick={() => onSubmit(lang, resourceConfig)}
        >
          Select
        </button>
        <button
          type="button"
          className="button-secondary"
          onClick={() => onCancel()}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export const CodeKernelFooter = ({
  kernelState = 'initial',
  doc,
  readOnlyWorksheet,
  worksheet,
  path,
}) => {
  const docId = doc.id;
  const [configModalVisible, setConfigModalVisible] = React.useState(false);
  // We only want to trigger a Kernel "ensure" and "heartbeat" if the user has actually
  // interacted with the page. This prevents us from spawning kernels for PCW workbooks
  // where the user is just looking at the class page. For breakout workbooks, kernels
  // will still be active because we prewarm them before class.
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);

  React.useEffect(() => {
    if (userHasInteracted) {
      return;
    }
    const handleUserInteraction = () => {
      setUserHasInteracted(true);
    };
    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    return () => {
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  React.useEffect(() => {
    initializeCodePresence(doc);
    if (!readOnlyWorksheet && userHasInteracted) {
      const usageInfo = {
        note: worksheet?.title,
      };
      ensureKernel(docId, usageInfo);
      window.onbeforeunload = () => {
        if (doc.data.preview) {
          destroyKernel(docId);
        }
      };
    }
  }, [readOnlyWorksheet, userHasInteracted]);

  // // Keepalive while page is visible.
  // React.useEffect(() => {
  //   const keepaliveInterval = setInterval(() => {
  //     if (document.visibilityState === 'visible' && userHasInteracted) {
  //       heartbeatKernel(docId);
  //     }
  //   }, KERNEL_KEEPALIVE_HEARTBEAT_INTERVAL_MSEC);

  //   return function cleanup() {
  //     clearInterval(keepaliveInterval);
  //   };
  // }, [userHasInteracted]);

  const kernelStateDisplayValue = (state) =>
    ({
      initial: 'waiting for user activity',
      idle: 'ready',
      unavailable: 'stopped',
    }[state] || state);
  const currentResourceConfig =
    doc.data.kernelConfig || DEFAULT_CODE_KERNEL_CONFIG;
  const currentResourceString =
    KERNEL_CONFIG_LIST[currentResourceConfig]?.resources || 'Unknown';
  const currentLang = doc.data.kernelLanguage || 'Python 3';

  return (
    <div
      className="secondary-font body-s flex justify-center fixed bottom-0"
      style={styles.container}
    >
      <div className="pt1 pb1 pr5 pl5" style={styles.content}>
        <div>
          {currentLang} ({currentResourceString})
          {!readOnlyWorksheet && (
            <>
              &nbsp;|&nbsp;
              <a
                type="button"
                style={styles.kernelCTA(false)}
                onClick={() => setConfigModalVisible(true)}
              >
                <span style={{ color: 'var(--blue-shade-20)' }}>Edit</span>
              </a>
            </>
          )}
          {configModalVisible && (
            <Modal
              onClose={() => setConfigModalVisible(false)}
              title="Change Configuration"
            >
              <KernelConfigChoice
                currentLang={currentLang}
                currentResourceConfig={currentResourceConfig}
                onCancel={() => setConfigModalVisible(false)}
                onSubmit={(newLang, newResourceConfig) => {
                  setConfigModalVisible(false);
                  if (newLang !== currentLang)
                    doc.submitOp({ p: ['kernelLanguage'], oi: newLang });
                  if (currentResourceConfig !== newResourceConfig)
                    doc.submitOp({
                      p: ['kernelConfig'],
                      oi: newResourceConfig,
                    });
                  changeKernel(
                    docId,
                    newLang || currentLang,
                    newResourceConfig || currentResourceConfig
                  );
                }}
              />
            </Modal>
          )}
        </div>
        {!readOnlyWorksheet && (
          <a
            type="button"
            style={styles.kernelCTA(false)}
            onClick={() => runAllCells(docId, worksheet, path)}
          >
            Run all cells
          </a>
        )}

        <div>
          <span
            className={`${
              !['idle', 'unavailable', 'initial'].includes(kernelState) &&
              'loading'
            }`}
            style={styles.kernelState}
          >
            Kernel {kernelStateDisplayValue(kernelState)}
          </span>
          {!readOnlyWorksheet && (
            <>
              &nbsp;|&nbsp;
              <KernelCTA kernelState={kernelState} docId={docId} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
